import { useAtomValue } from 'jotai';

import type { CourseRegistrationEnrollmentParentViewModel } from '@/shared/api/generated';
import { useGetApiV1CourseregistrationEnrollmentGrid } from '@/shared/api/generated';

import {
  enrollmentAppliedFiltersAtom,
  hasAnyEnrollmentFilter,
} from '../store/atoms/enrollmentFiltersAtom';
import { mapEnrollmentFiltersToParams } from '../utils/mapEnrollmentFiltersToParams';

type GridResponse = {
  parents: readonly CourseRegistrationEnrollmentParentViewModel[];
  totalCount: number;
  totalPages: number;
};

/**
 * O swagger do Kubb declara a resposta como `Parent[]`, mas o backend pode
 * envelopar em `{ items, totalPeopleCount, totalPages }` (descrição do endpoint
 * cita TotalPeopleCount). Esta função aceita ambos os formatos e extrai metadados
 * de paginação quando disponíveis.
 */
function parseGridResponse(data: unknown, pageSize: number): GridResponse {
  if (Array.isArray(data)) {
    const parents = data as readonly CourseRegistrationEnrollmentParentViewModel[];
    return { parents, totalCount: parents.length, totalPages: 1 };
  }

  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;

    const parents = (() => {
      if (Array.isArray(obj['items']))
        return obj['items'] as readonly CourseRegistrationEnrollmentParentViewModel[];
      if (Array.isArray(obj['data']))
        return obj['data'] as readonly CourseRegistrationEnrollmentParentViewModel[];
      if (Array.isArray(obj['parents']))
        return obj['parents'] as readonly CourseRegistrationEnrollmentParentViewModel[];
      return [] as readonly CourseRegistrationEnrollmentParentViewModel[];
    })();

    const totalCount =
      typeof obj['totalPeopleCount'] === 'number'
        ? obj['totalPeopleCount']
        : typeof obj['totalCount'] === 'number'
          ? obj['totalCount']
          : parents.length;

    const totalPages =
      typeof obj['totalPages'] === 'number'
        ? obj['totalPages']
        : typeof obj['pageCount'] === 'number'
          ? obj['pageCount']
          : Math.max(1, Math.ceil(totalCount / pageSize));

    return { parents, totalCount, totalPages };
  }

  return { parents: [], totalCount: 0, totalPages: 1 };
}

/**
 * Hook para listar matrículas agrupadas por turma/polo (parents) com filtros server-side.
 * A query só é executada quando o usuário aplica filtros preenchidos via
 * `useEnrollmentFilters().applyFilters()`. Sem filtros aplicados, a query
 * permanece desabilitada e a tabela exibe estado inicial vazio.
 *
 * @param page - Página (1-based) — passada para o backend e ao TanStack Query
 * @param pageSize - Tamanho da página
 * @returns Lista de parents, estado de loading/erro, refetch e flag `hasSearched`
 */
export function useEnrollmentGrid(page: number, pageSize: number) {
  const appliedFilters = useAtomValue(enrollmentAppliedFiltersAtom);
  const enabled = appliedFilters !== null && hasAnyEnrollmentFilter(appliedFilters);
  const params = appliedFilters
    ? mapEnrollmentFiltersToParams(appliedFilters, page, pageSize)
    : undefined;

  const query = useGetApiV1CourseregistrationEnrollmentGrid(params, {
    query: { enabled },
  });

  const { parents, totalCount, totalPages } = enabled
    ? parseGridResponse(query.data, pageSize)
    : { parents: [], totalCount: 0, totalPages: 1 };

  return {
    parents,
    isLoading: enabled && query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    hasSearched: enabled,
    totalCount,
    totalPages,
  } as const;
}
